import React, { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";

import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Loader2, User, Lock, Key, Camera, Eye, EyeOff, 
  Copy, LogOut, ChevronRight, ShieldCheck, 
  UserCheckIcon
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { cn } from "../lib/utils";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Profile = () => {
  const { user, updateProfile, logout,refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    address: user?.address || "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [pinData, setPinData] = useState({
    currentPin: "",
    newPin: "",
    confirmPin: "",
  });

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Account number copied!");
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(profileData);
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API_URL}/user/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success("Password changed successfully");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = async (e) => {
    e.preventDefault();
    if (pinData.newPin !== pinData.confirmPin) {
      toast.error("PINs don't match");
      return;
    }
    if (pinData.newPin.length !== 4) {
      toast.error("PIN must be 4 digits");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API_URL}/user/change-pin`, {
        current_pin: pinData.currentPin,
        new_pin: pinData.newPin,
      });
      toast.success("PIN changed successfully");
      setPinData({ currentPin: "", newPin: "", confirmPin: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change PIN");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Optional: Basic client-side validation
    if (file.size > 2 * 1024 * 1024) {
      return toast.error("File is too large. Max 2MB allowed.");
    }

    const formData = new FormData();
    formData.append("profilePicture", file);

    setUploading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/user/upload-profile`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      toast.success("Profile picture updated!");
      if (refreshUser) refreshUser(); // Refresh user data to show new image
    } catch (err) {
      console.log(err.response)
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700" data-testid="profile-page">
      
<section className="relative overflow-hidden bg-slate-900 rounded-[3rem] p-8 text-white shadow-2xl shadow-slate-200">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 blur-3xl rounded-full -mr-20 -mt-20" />
      
      {/* Hidden File Input */}
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleUpload} 
        className="hidden" 
        accept="image/*" 
      />

      <div className="relative flex flex-col items-center text-center space-y-4 text-white">
        <div className="relative group">
          <div className="absolute -inset-1.5 bg-white  rounded-full blur opacity-20 group-hover:opacity-40 transition duration-1000" />
          
          <Avatar className="w-24 h-24 border-4 border-slate-800 shadow-2xl">
            <AvatarImage src={user?.profile_picture} className="object-cover" />
            <AvatarFallback className="text-xl bg-slate-800 font-black">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </AvatarFallback>
          </Avatar>

          {/* Upload Button */}
          <button 
            disabled={uploading}
            onClick={() => fileInputRef.current.click()}
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-lg active:scale-90 transition-all hover:bg-slate-100 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
        </div>

        <div>
          <h2 className="text-xl font-black tracking-tight">{user?.first_name} {user?.last_name}</h2>
          <p className="text-slate-400 text-sm font-medium tracking-tight mt-0.5">{user?.email}</p>
        </div>

        {/* Account Number Copy (Assuming copyToClipboard is defined) */}
        <div 
          onClick={() => {
            navigator.clipboard.writeText(user?.account_number);
            toast.success("Copied to clipboard");
          }}
          className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-5 py-2.5 rounded-2xl cursor-pointer hover:bg-white/15 transition-all active:scale-95"
        >
          <div className="text-left">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Account Number</p>
            <p className="text-sm font-mono font-bold tracking-widest">{user?.account_number || "0000000000"}</p>
          </div>
          <Copy size={14} className="text-slate-400" />
        </div>
      </div>
    </section>

      {/* Settings Navigation */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="bg-slate-100/70 p-1.5 rounded-[2rem] w-full grid grid-cols-3 mb-8">
          <TabsTrigger value="profile" className="rounded-full py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm font-black text-[12px] uppercase tracking-widest" data-testid="profile-tab">
            <User className="w-3.5 h-3.5 mr-2" /> Profile
          </TabsTrigger>
          <TabsTrigger value="password" className="rounded-full py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm font-black text-[12px] uppercase tracking-widest" data-testid="password-tab">
            <Lock className="w-3.5 h-3.5 mr-2" /> Password
          </TabsTrigger>
          <TabsTrigger value="pin" className="rounded-full py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm font-black text-[12px] uppercase tracking-widest" data-testid="pin-tab">
            <Key className="w-3.5 h-3.5 mr-2" /> PIN
          </TabsTrigger>
        </TabsList>

        {/* Profile Details Tab */}
        <TabsContent value="profile">
          <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden">
            <CardHeader className="px-8 pt-8">
              <CardTitle className="font-black text-slate-900 uppercase text-sm tracking-widest">Personal Information</CardTitle>
              <CardDescription className="text-[11px] font-bold uppercase text-slate-400">Update your profile details</CardDescription>
            </CardHeader>
            <CardContent className="px-8 pb-8 pt-4">
              <form onSubmit={handleProfileUpdate} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">First Name</Label>
                    <Input className="rounded-2xl bg-slate-50 border-none h-12 font-bold focus-visible:ring-slate-900" value={profileData.first_name} onChange={(e) => setProfileData(p => ({ ...p, first_name: e.target.value }))} data-testid="profile-first-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">Last Name</Label>
                    <Input className="rounded-2xl bg-slate-50 border-none h-12 font-bold focus-visible:ring-slate-900" value={profileData.last_name} onChange={(e) => setProfileData(p => ({ ...p, last_name: e.target.value }))} data-testid="profile-last-name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">Phone</Label>
                  <Input className="rounded-2xl bg-slate-50 border-none h-12 font-bold focus-visible:ring-slate-900" value={profileData.phone} onChange={(e) => setProfileData(p => ({ ...p, phone: e.target.value }))} data-testid="profile-phone" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">Address</Label>
                  <Input className="rounded-2xl bg-slate-50 border-none h-12 font-bold focus-visible:ring-slate-900" value={profileData.address} onChange={(e) => setProfileData(p => ({ ...p, address: e.target.value }))} data-testid="profile-address" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-14 rounded-[1.5rem] bg-slate-900 hover:bg-slate-800 text-sm font-black uppercase tracking-widest transition-all active:scale-95 shadow-xl shadow-slate-100" data-testid="save-profile-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Changes"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password">
          <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden">
            <CardHeader className="px-8 pt-8">
              <CardTitle className="font-black text-slate-900 uppercase text-sm tracking-widest">Change Password</CardTitle>
              <CardDescription className="text-[11px] font-bold uppercase text-slate-400">Update your account password</CardDescription>
            </CardHeader>
            <CardContent className="px-8 pb-8 pt-4">
              <form onSubmit={handlePasswordChange} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">Current Password</Label>
                  <div className="relative">
                    <Input type={showPassword ? "text" : "password"} className="rounded-2xl bg-slate-50 border-none h-12 font-bold pr-12 focus-visible:ring-slate-900" value={passwordData.currentPassword} onChange={(e) => setPasswordData(p => ({ ...p, currentPassword: e.target.value }))} data-testid="current-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">New Password</Label>
                  <Input type={showPassword ? "text" : "password"} className="rounded-2xl bg-slate-50 border-none h-12 font-bold focus-visible:ring-slate-900" value={passwordData.newPassword} onChange={(e) => setPasswordData(p => ({ ...p, newPassword: e.target.value }))} data-testid="new-password" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">Confirm New Password</Label>
                  <Input type={showPassword ? "text" : "password"} className="rounded-2xl bg-slate-50 border-none h-12 font-bold focus-visible:ring-slate-900" value={passwordData.confirmPassword} onChange={(e) => setPasswordData(p => ({ ...p, confirmPassword: e.target.value }))} data-testid="confirm-new-password" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-14 rounded-[1.5rem] bg-slate-900 hover:bg-slate-800 text-sm font-black uppercase tracking-widest transition-all active:scale-95 shadow-xl shadow-slate-100" data-testid="change-password-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Change Password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PIN Tab */}
        <TabsContent value="pin">
          <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden">
            <CardHeader className="px-8 pt-8">
              <CardTitle className="font-black text-slate-900 uppercase text-sm tracking-widest">Change Transaction PIN</CardTitle>
              <CardDescription className="text-[11px] font-bold uppercase text-slate-400">Update your 4-digit transaction PIN</CardDescription>
            </CardHeader>
            <CardContent className="px-8 pb-8 pt-4">
              <form onSubmit={handlePinChange} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">Current PIN</Label>
                  <Input type="password" maxLength={4} value={pinData.currentPin} onChange={(e) => setPinData(p => ({ ...p, currentPin: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="****" className="rounded-2xl bg-slate-50 border-none h-14 font-black text-xl text-center tracking-[1em] focus-visible:ring-slate-900" data-testid="current-pin" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">New PIN</Label>
                    <Input type="password" maxLength={4} value={pinData.newPin} onChange={(e) => setPinData(p => ({ ...p, newPin: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="****" className="rounded-2xl bg-slate-50 border-none h-14 font-black text-xl text-center tracking-[1em] focus-visible:ring-slate-900" data-testid="new-pin" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[12px] font-black uppercase text-slate-400 ml-1">Confirm New PIN</Label>
                    <Input type="password" maxLength={4} value={pinData.confirmPin} onChange={(e) => setPinData(p => ({ ...p, confirmPin: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="****" className="rounded-2xl bg-slate-50 border-none h-14 font-black text-xl text-center tracking-[1em] focus-visible:ring-slate-900" data-testid="confirm-new-pin" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-14 rounded-[1.5rem] bg-slate-900 hover:bg-slate-800 text-sm font-black uppercase tracking-widest transition-all active:scale-95 shadow-xl shadow-slate-100" data-testid="change-pin-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Change PIN"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Logout Action Tile */}

           {/* Logout Action Tile */}
      <Link 
        to={'/contact'}
        className="w-full group flex items-center justify-between p-6 bg-blue-50 hover:bg-blue-100 rounded-[2.5rem] transition-all duration-300 border border-blue-100"
        data-testid="logout-btn"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-blue-600 shadow-sm group-hover:rotate-12 transition-transform">
            <LogOut size={20} />
          </div>
          <div className="text-left">
            <p className="font-black text-blue-600 text-sm uppercase tracking-widest">Contact Us</p>
            <p className="text-blue-400 text-[12px] font-bold uppercase tracking-tighter">Need Help? You can easily contact us</p>
          </div>
        </div>
        <ChevronRight size={20} className="text-blue-200" />
      </Link>

    {Boolean(user?.is_admin) && (
      <Link 
        to={'/admin'}
        className="w-full group flex items-center justify-between p-6 bg-slate-100 hover:bg-slate-200 rounded-[2.5rem] transition-all duration-300 border border-slate-100"
        data-testid="logout-btn"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-slate-900 shadow-sm group-hover:rotate-12 transition-transform">
            <UserCheckIcon size={20} />
          </div>
          <div className="text-left">
            <p className="font-black text-slate-900 text-sm uppercase tracking-widest">Access Admin</p>
            <p className="text-slate-900 text-[12px] font-bold uppercase tracking-tighter">Go to Admin Panel</p>
          </div>
        </div>
        <ChevronRight size={20} className="text-slate-600" />
      </Link>
      )}


      <button 
        onClick={logout}
        className="w-full group flex items-center justify-between p-6 bg-red-50 hover:bg-red-100 rounded-[2.5rem] transition-all duration-300 border border-red-100"
        data-testid="logout-btn"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-red-600 shadow-sm group-hover:rotate-12 transition-transform">
            <LogOut size={20} />
          </div>
          <div className="text-left">
            <p className="font-black text-red-600 text-sm uppercase tracking-widest">Sign Out</p>
            <p className="text-red-400 text-[12px] font-bold uppercase tracking-tighter">Securely end your session</p>
          </div>
        </div>
        <ChevronRight size={20} className="text-red-200" />
      </button>
      {/* Trust Footer */}
      <div className="flex flex-col items-center justify-center space-y-2 pt-4 opacity-60">
        <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-100">
          <ShieldCheck size={14} className="text-emerald-600" />
          <span className="text-[11px] font-black text-emerald-600 uppercase tracking-widest">End-to-End Encrypted Access</span>
        </div>
        <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest">Version 2.0.4 • 2026 Stable Build</p>
      </div>

    </div>
  );
};